'use client'

import { BUTTON_VARIANT } from '@fukku/button/Button.types'
import { ButtonContentLink } from '@fukku/button/ContentLink/ButtonContentLink'
import { BottomSheet } from '@fukku/dialog/BottomSheet/BottomSheet'
import type { DialogAPI } from '@fukku/dialog/Dialog'
import {
	SideSheet,
	type SideSheetProps,
} from '@fukku/dialog/SideSheet/SideSheet'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import dynamic from 'next/dynamic'
import { dataTestIds } from 'pdp/src/constants/dataTestIds/dataTestIds'
import { useAnalytics } from 'pdp/src/contexts/AnalyticsProvider/AnalyticsProvider'
import type { CommonProps } from 'pdp/src/types/CommonProps'
import { PdpActionEvents } from 'pdp/src/types/PdpActionEvents'
import {
	type ForwardRefExoticComponent,
	type ReactNode,
	type RefAttributes,
	useRef,
} from 'react'
import { useResponsiveBehavior } from 'responsive/hooks/useResponsiveBehavior/useResponsiveBehavior'
import styles from './ProductDetailsLink.module.css'

export interface ProductDetailsLinkProps
	extends Pick<CommonProps, 'color' | 'product'> {}

const ProductDetailsSheetContent = dynamic(() =>
	import('../ProductDetailsSheet/ProductDetailsSheet').then(
		(module) => module.ProductDetailsSheet
	)
)

export function ProductDetailsLink({
	product,
	color,
}: ProductDetailsLinkProps): ReactNode {
	const { t } = useLabels()
	const { sendPdpActionEvent } = useAnalytics()
	const modalRef = useRef<DialogAPI>(null)

	const ResponsiveDialog = useResponsiveBehavior<
		ForwardRefExoticComponent<SideSheetProps & RefAttributes<DialogAPI>>
	>({
		default: SideSheet,
		small: BottomSheet,
	})

	const openModal = () => {
		sendPdpActionEvent(PdpActionEvents.CLICK_PRODUCT_DETAILS)
		modalRef.current?.show()
	}

	return (
		<div className={styles.container}>
			<ButtonContentLink
				variant={BUTTON_VARIANT.DEFAULT}
				className={styles.link}
				onClick={() => openModal()}
				data-testid={dataTestIds['pdp.productInfo.productDetailsLink']}
			>
				{t('pdp.seeDetails.link')}
			</ButtonContentLink>
			<ResponsiveDialog title='' ref={modalRef}>
				<ProductDetailsSheetContent color={color} product={product} />
			</ResponsiveDialog>
		</div>
	)
}
